import { z } from 'zod';

// Bestanden bij een bericht in de chat (slepen, plakken of kiezen): welke soorten Jerome leest, de grenzen per bestand en
// per bericht, en de vaste zinnen bij een geweigerd bestand. Client en server gebruiken dezelfde regels: het scherm weigert
// vooraf op naam, type en grootte, de server beslist daarna op de bytes. Een weigering van de server toont het scherm als
// vaste zin met een kort kenmerk; de echte reden staat alleen in het serverlog, bij dat kenmerk.

export const ChatFileKind = z.enum(['image', 'pdf', 'word', 'excel', 'powerpoint', 'text']);
export type ChatFileKind = z.infer<typeof ChatFileKind>;

/** Een bestand bij een bericht (`content.files`): wat het gesprek en de beurt nodig hebben. De bytes staan apart op de
 * server en komen via GET /api/chats/:id/files/:fileId. */
export const ChatFile = z.object({
  id: z.string(),
  name: z.string(),
  mediaType: z.string(),
  size: z.number().int().nonnegative(),
  kind: ChatFileKind,
  /** Het pad van de kopie in OneDrive (`<map>/<jjjj-mm-dd> <uu.mm> <naam>`), als die gelukt is; een ouder bericht heeft het niet. */
  path: z.string().optional(),
});
export type ChatFile = z.infer<typeof ChatFile>;

/** De bestanden bij een bericht; leeg als er geen zijn of als de inhoud niet klopt. */
export function chatFiles(m: { content: Record<string, unknown> }): ChatFile[] {
  const r = z.array(ChatFile).safeParse(m.content['files']);
  return r.success ? r.data : [];
}

/** Een bestand dat Jerome in een beurt in OneDrive opsloeg (save_file, save_attachment, copy_file, convert_to_pdf, sign_pdf), op het
 * eindbericht van die beurt (`content.savedFiles`, 2.2.56): naam en pad, voor de knop Open map onder het antwoord. */
export const SavedFile = z.object({ name: z.string().min(1).max(255), path: z.string().min(1).max(400) });
export type SavedFile = z.infer<typeof SavedFile>;

/** Hooguit zoveel opgeslagen bestanden per bericht. */
export const SAVED_FILES_MAX = 50;

/** De bestanden die Jerome bij een bericht opsloeg; leeg als er geen zijn of als de inhoud niet klopt. */
export function savedFiles(m: { content: Record<string, unknown> }): SavedFile[] {
  const r = z.array(SavedFile).max(SAVED_FILES_MAX).safeParse(m.content['savedFiles']);
  return r.success ? r.data : [];
}

/** De route in de app die een bestand in OneDrive opent, of met `map` de map waarin het staat (GET /api/files/link, 2.2.56). De server
 * haalt het adres zelf bij de connector; een link in een antwoord bevat dus nooit een adres uit het model. */
export function driveLink(path: string, map = false): string {
  return `/api/files/link?path=${encodeURIComponent(path)}${map ? '&map=1' : ''}`;
}

/** De map van een pad in OneDrive: alles vóór de laatste schuine streep, leeg voor de hoofdmap. */
export function folderOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i < 0 ? '' : path.slice(0, i);
}

const MB = 1024 * 1024;
const GB = 1024 * MB;
/** Grenzen: per bestand, per bericht (samen) en het aantal per bericht. Een afbeelding mag bij de API hooguit 5 MB zijn. */
export const CHAT_FILE_LIMITS = { maxFileBytes: 5 * MB, maxMessageBytes: 10 * MB, maxFiles: 10 } as const;

/** Grootte in gewone taal: "820 B", "14 kB", "1,2 MB", "3,4 GB" (sinds 2.3.5: de bestanden uit WhatsApp samen). */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${Math.max(1, Math.round(bytes / 1024))} kB`;
  // Vanaf wat afgerond 1024 MB zou zijn, in GB.
  const giga = bytes >= GB - MB / 2;
  const value = bytes / (giga ? GB : MB);
  return `${(value >= 10 ? Math.round(value).toString() : (Math.round(value * 10) / 10).toString()).replace('.', ',')} ${giga ? 'GB' : 'MB'}`;
}

const EXTENSIONS: Record<string, ChatFileKind> = {
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
  pdf: 'pdf',
  docx: 'word',
  xlsx: 'excel',
  xlsm: 'excel',
  pptx: 'powerpoint',
  txt: 'text',
  csv: 'text',
  tsv: 'text',
  md: 'text',
  json: 'text',
  log: 'text',
  xml: 'text',
  yaml: 'text',
  yml: 'text',
};
export const WORD_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
export const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const POWERPOINT_TYPE = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
const MEDIA_TYPES: Record<string, ChatFileKind> = {
  'image/png': 'image',
  'image/jpeg': 'image',
  'image/gif': 'image',
  'image/webp': 'image',
  'application/pdf': 'pdf',
  [WORD_TYPE]: 'word',
  [EXCEL_TYPE]: 'excel',
  'application/vnd.ms-excel.sheet.macroenabled.12': 'excel',
  [POWERPOINT_TYPE]: 'powerpoint',
  'application/json': 'text',
  'application/xml': 'text',
};

/** De soort op grond van naam en opgegeven type (de extensie gaat voor); null als Jerome dit soort bestand niet leest.
 * Een geplakte schermafbeelding heeft soms geen extensie, wel het type image/png. Html telt niet als tekst. */
export function chatFileKind(name: string, type: string): ChatFileKind | null {
  const ext = /\.([a-z0-9]+)$/i.exec(name.trim())?.[1]?.toLowerCase();
  if (ext && EXTENSIONS[ext]) return EXTENSIONS[ext];
  const t = (type.split(';')[0] ?? '').trim().toLowerCase();
  if (MEDIA_TYPES[t]) return MEDIA_TYPES[t];
  if (t.startsWith('text/') && t !== 'text/html') return 'text';
  return null;
}

/** Voor het attribuut `accept` van de bestandskiezer. */
export const CHAT_FILE_ACCEPT = [...Object.keys(EXTENSIONS).map((e) => `.${e}`), 'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf', WORD_TYPE, EXCEL_TYPE, POWERPOINT_TYPE, 'text/plain', 'text/csv'].join(',');

export const ChatFileProblem = z.enum(['type', 'size', 'count', 'total', 'unreadable', 'failed']);
export type ChatFileProblem = z.infer<typeof ChatFileProblem>;

/** De vaste zinnen bij een geweigerd bestand: nooit de tekst van de server of van een omzetting. */
export const CHAT_FILE_ERRORS: Record<ChatFileProblem, string> = {
  type: 'Dit soort bestand kan Jerome niet lezen; wel afbeeldingen, pdf, Word, Excel, PowerPoint en tekst.',
  size: `Dit bestand is te groot: hooguit ${formatFileSize(CHAT_FILE_LIMITS.maxFileBytes)} per bestand.`,
  count: `Te veel bestanden: hooguit ${CHAT_FILE_LIMITS.maxFiles} per bericht.`,
  total: `Samen te groot: hooguit ${formatFileSize(CHAT_FILE_LIMITS.maxMessageBytes)} per bericht.`,
  unreadable: 'Dit bestand is niet te lezen.',
  failed: 'Dit bestand is niet bewaard; probeer het opnieuw.',
};

/** De grens zoals de chat hem toont (bij de bestanden boven het vak en in de sleeprand). */
export const CHAT_FILE_HINT = `Afbeeldingen, pdf, Word, Excel, PowerPoint en tekst · hooguit ${formatFileSize(CHAT_FILE_LIMITS.maxFileBytes)} per bestand, ${CHAT_FILE_LIMITS.maxFiles} bestanden en ${formatFileSize(CHAT_FILE_LIMITS.maxMessageBytes)} per bericht`;

export interface ChatFileCandidate {
  name: string;
  type: string;
  size: number;
}

/** Per bestand het probleem of null, in volgorde: soort, grootte, aantal en samen. Een geweigerd bestand telt niet mee voor
 * het aantal en de som, zodat de rest gewoon mee kan. */
export function checkChatFiles(files: readonly ChatFileCandidate[]): Array<ChatFileProblem | null> {
  let count = 0;
  let total = 0;
  return files.map((f) => {
    if (!chatFileKind(f.name, f.type)) return 'type';
    if (f.size > CHAT_FILE_LIMITS.maxFileBytes) return 'size';
    if (f.size <= 0) return 'unreadable';
    if (count + 1 > CHAT_FILE_LIMITS.maxFiles) return 'count';
    if (total + f.size > CHAT_FILE_LIMITS.maxMessageBytes) return 'total';
    count += 1;
    total += f.size;
    return null;
  });
}

/** De tekst bij een bericht met bestanden (formulier): mag leeg zijn, want de bestanden zijn dan het bericht. */
export const ChatFormText = z.string().trim().max(20000);

/** Het antwoord van de server bij een geweigerd bestand (400): de vaste zin, de soort weigering, het kenmerk uit het serverlog en
 * de plaats van het bestand in het verzoek. */
export const ChatFileRejection = z.object({
  error: z.string(),
  problem: ChatFileProblem,
  kenmerk: z.string(),
  index: z.number().int(),
});
export type ChatFileRejection = z.infer<typeof ChatFileRejection>;
